import React, { useEffect, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../store";
import { useMsal } from "@azure/msal-react";
import Sidebar from "../../../../components/Sidebar";
import styles from "./jobsRoles.module.scss";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PageLoader from "../../../../components/PageLoader";
import {
  fetchInterviewHostDashboard,
  Job,
} from "../../../../store/interviewHostSlice";

// Type Definitions
interface MetricCard {
  value: string;
  label: string;
  subtext: string;
}

type BadgeColor = "warning" | "success" | "info" | "error";

interface StatusBadge {
  text: string;
  color: BadgeColor;
}

// Utility Functions - Moved outside component for better performance
const parsePipelineLabel = (label: string): StatusBadge => {
  const lowerLabel = label.toLowerCase();

  if (lowerLabel.includes("to review") || lowerLabel.includes("report ready")) {
    return { text: label, color: "warning" };
  }
  if (lowerLabel.includes("approved") || lowerLabel.includes("completed")) {
    return { text: label, color: "success" };
  }
  if (lowerLabel.includes("invited")) {
    return { text: label, color: "info" };
  }
  if (lowerLabel.includes("declined") || lowerLabel.includes("decided")) {
    return { text: label, color: "error" };
  }

  return { text: label, color: "info" };
};

const getStatusClassName = (status: string): string => {
  return status.toLowerCase() === "active"
    ? styles.statusOpen
    : styles.statusOnHold;
};

const getBadgeClassName = (color: BadgeColor): string => {
  const badgeMap: Record<BadgeColor, string> = {
    warning: styles.badgeWarning,
    success: styles.badgeSuccess,
    info: styles.badgeInfo,
    error: styles.badgeError,
  };
  return badgeMap[color] || styles.badgeWarning;
};

// Memoized Sub-Components
const MetricCardComponent = memo(({ metric }: { metric: MetricCard }) => (
  <div className={styles.metricCard}>
    <div className={styles.metricValue}>{metric.value}</div>
    <div className={styles.metricLabel}>{metric.label}</div>
    <div className={styles.metricSubtext}>{metric.subtext}</div>
  </div>
));
MetricCardComponent.displayName = "MetricCard";

interface JobCardProps {
  job: Job;
  onJobClick: (jobId: string) => void;
}

const JobCardComponent = memo(({ job, onJobClick }: JobCardProps) => {
  const statusBadge = useMemo(() => parsePipelineLabel(job.pipeline_label), [job.pipeline_label]);

  const handleClick = useCallback(() => {
    onJobClick(job.job_id);
  }, [job.job_id, onJobClick]);

  return (
    <div className={styles.jobCard} onClick={handleClick}>
      <div className={styles.jobHeader}>
        <div className={styles.jobHeaderLeft}>
          <span
            className={`${styles.status} ${getStatusClassName(job.status)}`}
          >
            {job.status}
          </span>
          <span className={styles.postedTime}>Posted {job.posted_days_ago}d ago</span>
        </div>
      </div>

      <h3 className={styles.jobTitle}>{job.title}</h3>

      <p className={styles.jobMeta}>
        {job.department} · {job.location}
        {job.business_unit && ` · ${job.business_unit}`}
      </p>

      <div className={styles.jobFooter}>
        <div className={styles.jobFooterLeft}>
          <PeopleOutlineIcon sx={{ fontSize: 16 }} />
          <span>{job.candidate_count} candidates</span>
        </div>
        <div
          className={`${styles.badge} ${getBadgeClassName(statusBadge.color)}`}
        >
          {statusBadge.text}
        </div>
      </div>

      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${job.progress_percent}%` }}
        />
      </div>
    </div>
  );
});
JobCardComponent.displayName = "JobCard";

// Main Component
const JobsRoles: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: dashboardData, loading, error } = useSelector(
    (state: RootState) => state.interviewHost
  );

  // Ensure data is loaded
  useEffect(() => {
    if (!dashboardData) {
      dispatch(fetchInterviewHostDashboard());
    }
  }, [dashboardData, dispatch]);

  // Event handlers - Memoized to prevent unnecessary re-renders
  const handleLogout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  }, [instance]);

  const handleBackToHome = useCallback(() => {
    navigate("/agent/interview-host-agent");
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  const handleJobClick = useCallback(
    (jobId: string) => {
      navigate(`/agent/interview-host-agent/jobs/${jobId}/candidates`);
    },
    [navigate]
  );

  // Computed metric cards - Memoized to prevent recalculation
  const metricCards = useMemo<MetricCard[]>(() => {
    if (!dashboardData) return [];

    const { metrics } = dashboardData;
    const deltaSign = metrics.open_roles_delta_month > 0 ? "↑" : metrics.open_roles_delta_month < 0 ? "↓" : "";
    const avgTimeDeltaSign = metrics.average_time_to_invite_delta_days > 0 ? "↑" : metrics.average_time_to_invite_delta_days < 0 ? "↓" : "";

    return [
      {
        value: metrics.open_roles.toString(),
        label: "Open roles",
        subtext: `${deltaSign} ${Math.abs(metrics.open_roles_delta_month)} this month`,
      },
      {
        value: metrics.active_candidates.toString(),
        label: "Active candidates",
        subtext: "across all roles",
      },
      {
        value: metrics.interviews_this_week.toString(),
        label: "Interviews this week",
        subtext: `${metrics.interviews_completed_this_week} completed`,
      },
      {
        value: `${metrics.average_time_to_invite_days.toFixed(1)}d`,
        label: "Avg time to invite",
        subtext: `${avgTimeDeltaSign} ${Math.abs(metrics.average_time_to_invite_delta_days).toFixed(1)}d vs last month`,
      },
    ];
  }, [dashboardData]);

  // Loading state
  if (loading) {
    return <PageLoader label="Loading jobs..." activeNav="home" />;
  }

  // Error state
  if (error || !dashboardData) {
    return (
      <div className={styles.container}>
        <Sidebar
          activeNav="home"
          user={user || undefined}
          onLogout={handleLogout}
        />
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load dashboard</h2>
            <p>{error || "An unexpected error occurred."}</p>
            <button className={styles.btnRefresh} onClick={handleRefresh}>
              <RefreshIcon sx={{ fontSize: 18 }} />
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Success state
  const { jobs } = dashboardData;

  return (
    <div className={styles.container}>
      <Sidebar
        activeNav="home"
        user={user || undefined}
        onLogout={handleLogout}
      />
      <main className={styles.mainContent}>
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={handleBackToHome}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>Jobs</span>
        </div>

        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Jobs & Roles</h1>
            <p className={styles.subtitle}>
              All open requisitions — click a job to see its candidates
            </p>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.btnRefresh}
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
              Refresh
            </button>
          </div>
        </div>

        <div className={styles.metricsGrid}>
          {metricCards.map((metric, index) => (
            <MetricCardComponent key={`metric-${index}`} metric={metric} />
          ))}
        </div>

        <div className={styles.jobsGrid}>
          {jobs.map((job) => (
            <JobCardComponent
              key={job.job_id}
              job={job}
              onJobClick={handleJobClick}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default memo(JobsRoles);
